import {
  AttendeeHistory,
  AttendeeHistoryCreateData,
} from "@/types/attendeeHistory";
import { parseDecimalNumber } from "@/utils/number";
import { normalizeWeightKgForPayload } from "@/utils/petWeight";
import { apiUrl } from "../../utils/url";
import { ATTENDED_APPOINTMENT_STATUSES } from "@/utils/appointmentStatuses";
import type { IAppointmentsService } from "../appointments";
import { HttpClient, HttpError } from "../HttpClient";
import { toServiceError } from "../errors";
import {
  AttendeeWeightHistoryParams,
  AttendeeWeightRecord,
  IAttendeeHistoryService,
} from "./IAttendeeHistoryService";

const LOAD_HISTORY_MESSAGE =
  "Não foi possível carregar o histórico do animal. Atualize a página e tente novamente.";

/** The newest record is on one of the latest attended appointments. */
const LATEST_APPOINTMENTS_SCANNED = 5;

/**
 * Runs `read` once per key while it is under way; callers that ask meanwhile
 * share it. A request dropped from the map (after a write) never removes the
 * one that replaced it.
 */
const shareInFlight = <K, T>(
  inFlight: Map<K, Promise<T>>,
  key: K,
  read: () => Promise<T>,
): Promise<T> => {
  const pending = inFlight.get(key);
  if (pending) return pending;

  const request: Promise<T> = read().finally(() => {
    if (inFlight.get(key) === request) inFlight.delete(key);
  });
  inFlight.set(key, request);
  return request;
};

export class AttendeeHistoryService implements IAttendeeHistoryService {
  private httpClient: HttpClient;

  constructor(
    /** Where the latest record is looked for among the pet's appointments. */
    private readonly appointments: Pick<
      IAppointmentsService,
      "searchAttendeeAppointments"
    >,
  ) {
    this.httpClient = new HttpClient({
      baseURL: apiUrl,
      timeout: 10000,
      headers: {},
    });
  }

  /*
   * The backend no longer lists an attendee's records, so the newest one is
   * the newest of two lists. The weight history holds the records the pet form
   * writes, which carry no appointment, but skips any record without a weight.
   * Appointments carry their own record, weighed or not, and the search lists
   * them latest first. An appointment's record comes whole, as by its id; a
   * weight history entry has only the weight and profile, so it is read again.
   */
  getLatestAttendeeHistory(
    attendeeId: number,
  ): Promise<AttendeeHistory | null> {
    // Several screens ask at once on a pet's first load; they share one lookup.
    return shareInFlight(this.latestInFlight, attendeeId, () =>
      this.findLatestAttendeeHistory(attendeeId),
    );
  }

  private readonly latestInFlight = new Map<
    number,
    Promise<AttendeeHistory | null>
  >();

  /*
   * A read started before a write may answer with what the write replaced, so
   * whoever asks after it starts a read of their own.
   */
  private forgetInFlight(attendeeId: number | undefined): void {
    // An update that does not name its pet could be anyone's.
    if (attendeeId === undefined) {
      this.latestInFlight.clear();
      this.weightHistoryInFlight.clear();
      return;
    }
    this.latestInFlight.delete(attendeeId);
    this.weightHistoryInFlight.delete(String(attendeeId));
  }

  private async findLatestAttendeeHistory(
    attendeeId: number,
  ): Promise<AttendeeHistory | null> {
    const [weighedResult, attendedResult] = await Promise.allSettled([
      this.getAttendeeWeightHistory({ attendeeId: String(attendeeId) }),
      this.appointments.searchAttendeeAppointments(attendeeId, {
        page: 0,
        size: LATEST_APPOINTMENTS_SCANNED,
        statusCodeList: ATTENDED_APPOINTMENT_STATUSES,
        includeAttendeeHistory: true,
      }),
    ]);
    const weighed =
      weighedResult.status === "fulfilled" ? weighedResult.value : [];
    const attended =
      attendedResult.status === "fulfilled" ? attendedResult.value.content : [];
    type Candidate = {
      /** Epoch ms; -Infinity when the date is missing or unreadable. */
      registeredAt: number;
      read: () => Promise<AttendeeHistory>;
    };
    // Compared as instants: the two lists need not format the time alike. An
    // unreadable date never wins, so it cannot hide a readable newer one.
    const toInstant = (value?: string) => {
      const instant = value ? Date.parse(value) : NaN;
      return Number.isNaN(instant) ? -Infinity : instant;
    };
    const attendedCandidates: Candidate[] = attended.flatMap(
      ({ attendeeHistory, ...appointment }) =>
        attendeeHistory
          ? [
              {
                registeredAt: toInstant(attendeeHistory.registeredOn),
                read: async () => ({
                  ...attendeeHistory,
                  attendee: appointment.attendee,
                  appointment,
                  isCreation: false,
                }),
              },
            ]
          : [],
    );
    // A weighed check-in is in both lists; the copy already in hand is kept,
    // whatever precision each list gives its time.
    const inHand = new Set(
      attended.flatMap(({ attendeeHistory }) =>
        attendeeHistory ? [attendeeHistory.id] : [],
      ),
    );
    const weighedCandidates: Candidate[] = weighed
      .filter(record => !inHand.has(record.id))
      .map(record => ({
        registeredAt: toInstant(record.registeredOn),
        read: () => this.getAttendeeHistoryById(record.id),
      }));
    const newestOf = (list: Candidate[]) =>
      list.reduce<Candidate | null>(
        (newest, record) =>
          !newest || record.registeredAt > newest.registeredAt
            ? record
            : newest,
        null,
      );
    // The appointments come first: a weighed check-in is in both lists, and
    // on that tie the record already in hand is kept rather than read again.
    const latest = newestOf([...attendedCandidates, ...weighedCandidates]);
    if (!latest) {
      // Either list alone still names a record. A failed list that leaves
      // nothing to name is a failure, not a pet without records.
      const failed = [weighedResult, attendedResult].find(
        (result): result is PromiseRejectedResult =>
          result.status === "rejected",
      );
      if (failed) throw toServiceError(failed.reason, LOAD_HISTORY_MESSAGE);
      return null;
    }
    try {
      return await latest.read();
    } catch (error) {
      // Only a weight entry is read again. When that read fails, the newest
      // appointment record, already in hand, still names a record.
      const fallback = newestOf(attendedCandidates);
      if (!fallback) throw error;
      return fallback.read();
    }
  }

  /*
   * The pet form asks for the latest record and the latest weight at once, and
   * both read this list, so a request already under way is shared.
   */
  private readonly weightHistoryInFlight = new Map<
    string,
    Promise<AttendeeWeightRecord[]>
  >();

  getAttendeeWeightHistory(
    params: AttendeeWeightHistoryParams,
  ): Promise<AttendeeWeightRecord[]> {
    return shareInFlight(this.weightHistoryInFlight, params.attendeeId, () =>
      this.readAttendeeWeightHistory(params),
    );
  }

  private async readAttendeeWeightHistory(
    params: AttendeeWeightHistoryParams,
  ): Promise<AttendeeWeightRecord[]> {
    const endpoint = `/attendeeHistory/attendee/${params.attendeeId}/weight-history`;

    try {
      const response =
        await this.httpClient.get<AttendeeWeightRecord[]>(endpoint);

      return response;
    } catch (error) {
      throw toServiceError(
        error,
        "Não foi possível carregar o histórico de peso do animal. Atualize a página e tente novamente.",
      );
    }
  }

  async getAttendeeHistoryByAppointmentId(
    id: string,
  ): Promise<AttendeeHistory | null> {
    const endpoint = `/attendeeHistory/appointment/${id}`;
    try {
      const response = await this.httpClient.get<AttendeeHistory>(endpoint);
      return response;
    } catch (error) {
      if (error instanceof HttpError && error.status === 404) {
        return null;
      }
      throw toServiceError(error, LOAD_HISTORY_MESSAGE);
    }
  }

  async getAttendeeHistoryById(id: number): Promise<AttendeeHistory> {
    const endpoint = `/attendeeHistory/${id}`;
    try {
      const response = await this.httpClient.get<AttendeeHistory>(endpoint);
      return response;
    } catch (error) {
      throw toServiceError(error, LOAD_HISTORY_MESSAGE);
    }
  }

  async createAttendeeHistory(
    data: AttendeeHistoryCreateData,
  ): Promise<number> {
    const endpoint = `/attendeeHistory`;
    const payload = {
      ...data,
      weightKg: normalizeWeightKgForPayload(data.weightKg),
      tempCelsius: parseDecimalNumber(data.tempCelsius),
    };

    try {
      const response = await this.httpClient.post<number>(
        endpoint,
        payload,
        {},
        true,
      );
      this.forgetInFlight(data.attendeeId);

      return response;
    } catch (error) {
      throw toServiceError(
        error,
        "Não foi possível criar o histórico do animal. Confirme os campos obrigatórios e tente novamente.",
      );
    }
  }

  async updateAttendeeHistory(
    id: number,
    data: Partial<AttendeeHistoryCreateData>,
  ): Promise<number> {
    const payload = {
      ...data,
      weightKg: normalizeWeightKgForPayload(data.weightKg),
      tempCelsius: parseDecimalNumber(data.tempCelsius),
    };
    try {
      const responseId = await this.httpClient.put<number>(
        `/attendeeHistory/${id}`,
        payload,
        {},
        true,
      );
      this.forgetInFlight(data.attendeeId);
      return responseId;
    } catch (error) {
      throw toServiceError(
        error,
        "Não foi possível atualizar o histórico do animal. Confirme os campos obrigatórios e tente novamente.",
      );
    }
  }
}
